import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaPaperPlane, FaCheckCircle } from 'react-icons/fa';
import emailjs from 'emailjs-com';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', service: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const serviceIcons = {
    "AI Solutions": "🤖 AI Solutions",
    "Web Development": "🌐 Web Development",
    "Mobile Apps": "📱 Mobile Apps",
    "Cybersecurity": "🔒 Cybersecurity",
    "Cloud Services": "☁️ Cloud Services",
    "Quality Analysis": "🧪 Quality Analysis",
    "SEO": "📈 SEO",
    "Domain & Hosting": "🖥️ Domain & Hosting",
    "Other": "🛠️ Other"
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const serviceWithIcon = serviceIcons[formData.service] || formData.service;

    emailjs
      .send(
        "service_qio1iuc",     // Replace with EmailJS Service ID
        "template_7h2zri8",    // Replace with EmailJS Template ID
        {
          name: formData.name,
          email: formData.email,
          company: formData.company,
          service: serviceWithIcon,   // ✅ use service with icon
          message: formData.message,
        },
        "aeBAv5z-puy0YwT-s"      // Replace with EmailJS Public Key
      )
      .then(
        () => {
          setIsSubmitting(false);
          setIsSubmitted(true);
          setFormData({ name: '', email: '', company: '', service: '', message: '' });

          // Auto-hide success message after 5s
          setTimeout(() => setIsSubmitted(false), 5000);
        },
        (err) => {
          setIsSubmitting(false);
          setError("Failed to send message. Please try again.");
          console.error("EmailJS Error:", err);
        }
      );
  };

  const contactInfo = [
    { icon: <FaEnvelope />, title: "Email Us", detail: "info.deployx@gmail.com", link: "mailto:info@deployx.in" },
    { icon: <FaPhone />, title: "Call Us", detail: "+91 7600236927", link: "tel:+917600236927" },
    { icon: <FaPhone />, title: "Call Us", detail: "+91 6350462627", link: "tel:+916350462627" },
    { icon: <FaMapMarkerAlt />, title: "Visit Us", detail: "Office no 2, Farmer CHS Plot No.32, Sector No.12, Nr. Honda Service Centre, Kharghar, Navi Mumbai.", link: "https://maps.app.goo.gl/XHWj98ZjP6ffPUM58" }
  ];

  const services = ["AI Solutions", "Web Development", "Mobile Apps", "Cybersecurity", "Cloud Services", "Quality Analysis", "SEO", "Domain & Hosting", "Other"];

  return (
    <section id="contact">
      <div className="container">
        <div className="contact-layout">
          {/* Column 1: Main Heading */}
          <motion.div className="contact-heading-column"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Get In<br /><span>Touch</span></h2>
            <p>Ready to transform your business? Let's discuss how we can help you achieve your goals with our innovative solutions.</p>
          </motion.div>

          {/* Column 2: Contact Info */}
          <motion.div className="contact-info-column"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3>Let's Start a Conversation</h3>
            <p>We're here to help you succeed. Reach out to us through any of our channels below.</p>
            <div className="contact-methods">
              {contactInfo.map((info, index) => (
                <a href={info.link} key={index} className="contact-method-card">
                  <div className="method-icon">{info.icon}</div>
                  <div className="method-text">
                    <h4>{info.title}</h4>
                    <span>{info.detail}</span>
                  </div>
                </a>
              ))}
            </div>
          </motion.div>

          {/* Column 3: Contact Form */}
          <motion.div className="contact-form-column"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <div className="contact-form-card">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="company">Company</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    placeholder="Enter your company name"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="service">Service Interested In</label>
                  <select id="service" name="service" value={formData.service} onChange={handleChange}>
                    <option value="">Select a service</option>
                    {services.map((s, i) => (
                      <option key={i} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about your project"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>
                {error && <p className="error-message">{error}</p>}
                <button type="submit" className="submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : <><FaPaperPlane /> Send Message</>}
                </button>
              </form>

              {isSubmitted && (
                <motion.div
                  className="success-message"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <FaCheckCircle className="success-icon" />
                  <h3>Message Sent!</h3>
                  <p>Thanks for reaching out to DEPLOY-X. We'll get back to you soon.</p>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;


